import { Title } from '@/components/Elements/Title/Title';
import { useTranslation } from 'react-i18next';
import { SignUpForm } from '@/components/Views/SignUp/SignUpForm/SignUpForm';
import {
  RegisterAccountMutation,
  useRegisterAccountMutation,
} from '@/generated/graphql';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { setAuthTokensToLocalStorage } from '@/utils/auth';
import { PasswordNotification } from '@/components/Elements/PasswordNotif/PasswordNotification';
import { useRedirect } from '@/hooks/useRedirect';
import { useState } from 'react';
import { Error } from '@/enums/Error';
import { GraphQLErrorExtensions } from '@/types/GraphQLErrorExtensions';
import { HaveAnAccountLink } from '@/components/Elements/HaveAnAccountLink/HaveAnAccountLink';
import { useRedirectAppRouter } from '@/hooks/useRedirectAppRouter';
import { routes } from '@/constants/routes';
import { getGQLLocaleFromLocale } from '@/utils/locales';
import { CoBrandDetails } from '../CoBrandDetails/CoBrandDetails';
import { useAuthBranding } from '@/components/Providers/BrandingProvider/BrandingProvider';

type SignUpProps = {
  title?: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  autofillAndLock?: boolean;
  locale: string;
  invitationToken?: string;
  companyId?: number;
  projectId?: number;
  signInLink?: string;
  projectSubId?: string;
  onSignupSuccess?: () => void;
};

export function SignUp({
  title,
  email,
  givenName,
  familyName,
  autofillAndLock = false,
  locale,
  invitationToken,
  companyId,
  projectId,
  signInLink,
  projectSubId,
  onSignupSuccess,
}: SignUpProps) {
  const { t } = useTranslation();
  const router = useRedirectAppRouter();
  const redirectFromLocalStorage = useRedirect();
  const { handleMsgType } = useNotification();
  const [submitting, setSubmitting] = useState(false);
  const [signupEmail, setSignupEmail] = useState<string>();

  const projectCoBranding = useAuthBranding().branding;
  const hasCoBrandingEnabled = !!projectCoBranding;

  const handleAuthError = (error: GraphQLErrorExtensions) => {
    const { code, message } = error?.response;

    switch (code) {
      case Error.EXISTING_ACCOUNT: {
        handleMsgType({
          type: TNotification.error,
          title: t('signUp.failed'),
          message: `${t('signUp.yourAccountHasAlreadyBeenCreated')}`,
        });

        const userEmail = email || signupEmail;

        if (invitationToken) {
          // User should be redirected to login screen
          // if account with that email is already present for direct invite
          router.redirectToLoginWithEmail(userEmail);
        } else {
          // Add indirect invite params and email to enable auto accept of indirect invite
          // when user logs in.
          router.pushWithIndirectInvParams(
            routes.login,
            userEmail ? { email: encodeURIComponent(userEmail) } : {}
          );
        }

        break;
      }

      case Error.PASSWORD_TOO_WEAK: {
        handleMsgType({
          type: TNotification.error,
          title: t('signUp.failed'),
          message: t('signUp.passwordInvalid'),
        });
        break;
      }

      default: {
        handleMsgType({
          type: TNotification.error,
          title: t('signUp.failed'),
          message: message,
        });
        break;
      }
    }
  };

  const [registerAccount] = useRegisterAccountMutation({
    onCompleted: (data: RegisterAccountMutation) => {
      const { authCode } = data.ClientRegisterAccount;
      setAuthTokensToLocalStorage(authCode);

      // On indirect invite, accept the invitation after creating account
      if (onSignupSuccess) {
        onSignupSuccess();
      } else {
        redirectFromLocalStorage();
      }
    },
    onError: ({ graphQLErrors }) => {
      setSubmitting(false);
      graphQLErrors.forEach(({ extensions }) => {
        if (extensions) {
          handleAuthError(extensions?.exception as GraphQLErrorExtensions);
        }
      });
    },
  });

  function handleSignUp(
    email: string,
    password: string,
    givenName: string,
    familyName: string
  ) {
    setSubmitting(true);
    setSignupEmail(email);

    registerAccount({
      variables: {
        email: email,
        password: password,
        firstName: givenName,
        lastName: familyName,
        locale: getGQLLocaleFromLocale(locale),
        invitationToken: invitationToken,
        companyId: companyId,
        projectId: projectId,
      },
    });
  }

  return (
    <>
      {hasCoBrandingEnabled && (
        <CoBrandDetails projectCoBranding={projectCoBranding} />
      )}
      <Title
        title={title ?? t('signUp.title')}
        hasCobranding={hasCoBrandingEnabled}
        maintainSize={!hasCoBrandingEnabled}
      />
      <PasswordNotification />
      <SignUpForm
        email={email}
        givenName={givenName}
        familyName={familyName}
        submitting={submitting}
        handleSignUp={handleSignUp}
        autofillAndLock={autofillAndLock}
      />
      <HaveAnAccountLink signInLink={signInLink} />
    </>
  );
}
